import {
  PendingSignOffsAccidentsIncidentsPath,
  PendingSignOffsChecklistPath,
  PendingSignOffsMedicationsPath,
  RolesPath,
  UsersPath,
} from "./routePaths";

export const USER_ROLES_TABS = [
  { label: "Users", path: UsersPath },
  { label: "Roles", path: RolesPath },
];

export const getResidentRoute = (id: string, tab: string = "bio") =>
  `/residents/${id}/${tab}`;

export const RESIDENT_PANEL_TABS = [
  {
    label: "Handover",
    getPath: (id: string) => getResidentRoute(id, "handover"),
  },
  {
    label: "Bio",
    getPath: (id: string) => getResidentRoute(id, "bio"),
  },
  {
    label: "Reports",
    getPath: (id: string) => getResidentRoute(id, "reports"),
  },
  {
    label: "Calendar",
    getPath: (id: string) => getResidentRoute(id, "calendar"),
  },
  {
    label: "Allergies",
    getPath: (id: string) => getResidentRoute(id, "allergies"),
  },
  {
    label: "Medications",
    getPath: (id: string) => getResidentRoute(id, "medications"),
  },
  {
    label: "Care plans",
    getPath: (id: string) => getResidentRoute(id, "care-plans"),
  },
  {
    label: "Medical notes",
    getPath: (id: string) => getResidentRoute(id, "medical-notes"),
  },
  {
    label: "Parameters",
    getPath: (id: string) => getResidentRoute(id, "parameters"),
  },
  {
    label: "Barthel Score",
    getPath: (id: string) => getResidentRoute(id, "barthel-score"),
  },
  {
    label: "Accidents & Incidents",
    getPath: (id: string) => getResidentRoute(id, "accidents-and-incidents"),
  },
  {
    label: "History & vaccinations",
    getPath: (id: string) => getResidentRoute(id, "history"),
  },
  {
    label: "Admission",
    getPath: (id: string) => getResidentRoute(id, "admission"),
  },
  {
    label: "Assessment",
    getPath: (id: string) => getResidentRoute(id, "assessment"),
  },
  {
    label: "Portfolio",
    getPath: (id: string) => getResidentRoute(id, "portfolio"),
  },
  {
    label: "Other info",
    getPath: (id: string) => getResidentRoute(id, "other-info"),
  },
];

export interface ResidentTabChild {
  label: string;
  segment: string;
  getPath: (id: string) => string;
}

export interface ResidentTabItem {
  label: string;
  segment?: string;
  getPath: (id: string) => string;
  children?: ResidentTabChild[];
}

export const RESIDENT_GROUPED_TABS: ResidentTabItem[] = [
  {
    label: "Handover",
    segment: "handover",
    getPath: (id) => getResidentRoute(id, "handover"),
  },
  {
    label: "Bio",
    getPath: (id) => getResidentRoute(id, "admission"),
    children: [
      {
        label: "Admission",
        segment: "admission",
        getPath: (id) => getResidentRoute(id, "admission"),
      },
      {
        label: "Profile",
        segment: "bio",
        getPath: (id) => getResidentRoute(id, "bio"),
      },
      {
        label: "Status History",
        segment: "status-history",
        getPath: (id) => getResidentRoute(id, "status-history"),
      },
    ],
  },
  {
    label: "Medications",
    segment: "medications",
    getPath: (id) => getResidentRoute(id, "medications"),
  },
  {
    label: "Parameters",
    segment: "parameters",
    getPath: (id) => getResidentRoute(id, "parameters"),
  },
  {
    label: "Reports",
    segment: "reports",
    getPath: (id) => getResidentRoute(id, "reports"),
  },
  {
    label: "Medical Notes",
    segment: "medical-notes",
    getPath: (id) => getResidentRoute(id, "medical-notes"),
  },
  {
    label: "Calendar",
    segment: "calendar",
    getPath: (id) => getResidentRoute(id, "calendar"),
  },
  {
    label: "Assessments",
    getPath: (id) => getResidentRoute(id, "care-plans"),
    children: [
      {
        label: "Nursing Care Plan",
        segment: "care-plans",
        getPath: (id) => getResidentRoute(id, "care-plans"),
      },
      {
        label: "Barthel Score",
        segment: "barthel-score",
        getPath: (id) => getResidentRoute(id, "barthel-score"),
      },
      {
        label: "Portfolio",
        segment: "portfolio",
        getPath: (id) => getResidentRoute(id, "portfolio"),
      },
      {
        label: "MUST",
        segment: "must",
        getPath: (id) => getResidentRoute(id, "must"),
      },
      {
        label: "MMSE",
        segment: "mmse",
        getPath: (id) => getResidentRoute(id, "mmse"),
      },
      {
        label: "Braden Scale",
        segment: "braden-scale",
        getPath: (id) => getResidentRoute(id, "braden-scale"),
      },
      {
        label: "FRASE",
        segment: "frase",
        getPath: (id) => getResidentRoute(id, "frase"),
      },
    ],
  },
  {
    label: "Medical History",
    getPath: (id) => getResidentRoute(id, "allergies"),
    children: [
      {
        label: "Allergies",
        segment: "allergies",
        getPath: (id) => getResidentRoute(id, "allergies"),
      },
      {
        label: "Accidents & History",
        segment: "accidents-and-incidents",
        getPath: (id) => getResidentRoute(id, "accidents-and-incidents"),
      },
      {
        label: "Health History",
        segment: "history",
        getPath: (id) => getResidentRoute(id, "history"),
      },
      {
        label: "Urgent Concerns",
        segment: "urgent-concerns",
        getPath: (id) => getResidentRoute(id, "urgent-concerns"),
      },
    ],
  },
];

export interface ResolvedTabChild {
  label: string;
  path: string;
}

export interface ResolvedTabItem {
  label: string;
  path: string;
  children?: ResolvedTabChild[];
}

export function resolveGroupedTabs(id: string): ResolvedTabItem[] {
  return RESIDENT_GROUPED_TABS.map((tab) => ({
    label: tab.label,
    path: tab.getPath(id),
    children: tab.children?.map((child) => ({
      label: child.label,
      path: child.getPath(id),
    })),
  }));
}

export function findActiveTabIndices(
  tabs: ResolvedTabItem[],
  currentPath: string,
): { parentIndex: number; childIndex: number } {
  for (let i = 0; i < tabs.length; i++) {
    const tab = tabs[i];
    if (tab?.children) {
      for (let j = 0; j < tab.children.length; j++) {
        if (currentPath.includes(tab.children[j]!.path)) {
          return { parentIndex: i, childIndex: j };
        }
      }
    } else if (tab && currentPath.includes(tab.path)) {
      return { parentIndex: i, childIndex: -1 };
    }
  }
  return { parentIndex: -1, childIndex: -1 };
}

export const PENDING_SIGNOFFS_TABS = [
  {
    key: "admission-checklist",
    label: "Admission Checklist",
    path: PendingSignOffsChecklistPath,
  },
  {
    key: "medications",
    label: "Medications",
    path: PendingSignOffsMedicationsPath,
  },
  {
    key: "accidents-and-incidents",
    label: "Accidents and Incidents",
    path: PendingSignOffsAccidentsIncidentsPath,
  },
];
